// src/app/argent/useProjectionArgent.js — la projection de facturation (V2),
// calc, « À facturer ».
// CHEMIN ARGENT. Déplacé octet pour octet depuis src/App.jsx (P-3.5, squelette,
// partie 2) : seuls changent l'emballage (signature, retour) et les imports.

import { useMemo, useRef, useCallback } from 'react'
import { MONTHS } from '../../lib/format'
import { getAllLogsFlat } from '../../lib/sousloc-helpers'
import { calc as calcPure, calcDirectInternePure, mergeCalc } from '../../lib/calc'
import { usePricingBlocksHistory } from '../../lib/use-pricing-blocks-history'
import { useResasV2 } from '../../lib/use-resas-v2'
import { collectMenageOverrides, collectMontantOverrides, projectDataForFacturation } from '../../lib/data-projection'
import { getFacturationSource } from '../../lib/facturation-source'
import { totauxFacture, netProprietaireFacture } from '../../lib/facture-html'

export function useProjectionArgent({ cfg, data, invoicesRaw, incidents, month, ready, currentUser, y, m }) {
  // ── PR #169b (ADR-106) — Hook chargement pricing_blocks "résa direct interne" ──
  // Indexation Map<mk, Map<lid, Array<block>>>. Pas de bornage temporel : on
  // charge tout (volumétrie attendue : qq dizaines). Filtre serveur :
  // guest_first_name NOT NULL + total_amount NOT NULL + active=true.
  // `enabled` gating sur ready + admin uniquement (le rôle menage n'a aucun
  // usage de la compta + RLS bloquerait de toute façon).
  const pricingBlocksEnabled = ready && currentUser?.role === 'admin'
  const { blocksByMonth, invalidate: invalidatePricingBlocks } = usePricingBlocksHistory(pricingBlocksEnabled)

  // ── WAGON M (M3.2/M3.3) — Câblage facturation source 'v2' (sync Beds24) ──
  //
  // v2Lids = logements (logs feuilles + chambres sous-loc lc1/lc2/lc3) dont la
  // source de facturation est 'v2'. OBLIGATOIREMENT via getFacturationSource
  // (défaut 'kv' = comportement historique ; jamais log.facturationSource direct).
  // getAllLogsFlat expose chambres sous-loc (où vivent les résas lc*) + logs
  // feuilles, dédup byId. État prod : chambres lc1/lc2/lc3 basculées 'v2'
  // (sous-loc Maison Zen) → v2Lids = ['lc1','lc2','lc3'] ; l1-l8 restent 'kv'.
  const v2Lids = useMemo(
    () =>
      getAllLogsFlat(cfg)
        .filter((l) => getFacturationSource(l) === 'v2')
        .map((l) => l.id),
    [cfg]
  )

  // Hook I/O : charge les rows reservations V2 des seuls logements 'v2'.
  // enabled gardé sur ready + admin + au moins un logement basculé (sinon 0 fetch).
  const resasV2Enabled = ready && currentUser?.role === 'admin' && v2Lids.length > 0
  // invalidate() exposé par le hook mais non câblé ici (aucun mutateur V2 côté
  // admin pour l'instant — le sync Beds24 écrit côté serveur). À brancher si une
  // mutation de résa V2 doit rafraîchir la projection sans recharger la page.
  const { resasByLogement } = useResasV2(resasV2Enabled, v2Lids)

  // Collecteur des résas V2 écartées (annulées, source tierce, etc.) par (lid, mk),
  // exposé pour l'UI M3.5 (écran de décision manuelle). Rempli à chaque projection.
  const skippedV2Ref = useRef({})

  // Lot 2 V2-delete-resa : collecteur des résas RETIRÉES par suppression V2 par
  // (lid, mk), repeuplé à chaque projection — affichage admin grisé/barré +
  // bouton « ↩️ Restaurer ». Miroir de skippedV2Ref.
  const tombstonedV2Ref = useRef({})

  // Mois à projeter : tous les mois présents dans `data` + mois courant + mois
  // suivant (lu par le previsionnel d'occupation). Union dédupliquée.
  const projectionMonthKeys = useMemo(() => {
    const set = new Set(Object.keys(data || {}))
    set.add(month)
    const [py, pm] = month.split('-').map(Number)
    const nd = new Date(py, pm, 1)
    set.add(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}`)
    // Lot 3 V2-move-resa : ajoute les mois CIBLES des déplacements
    // (data._monthOverrides[lid] = { code: 'YYYY-MM' }) pour les lid 'v2', afin
    // que la projection (IN) ait un mk où écrire la résa déplacée. L'imputation
    // effective unique garantit la correctness même si un mois manquait ; l'union
    // sert l'AFFICHAGE. Filtre 'YYYY-MM'.
    const mo = data && data._monthOverrides
    if (mo && typeof mo === 'object' && Array.isArray(v2Lids)) {
      for (const lid of v2Lids) {
        const lidMap = mo[lid]
        if (!lidMap || typeof lidMap !== 'object') continue
        for (const target of Object.values(lidMap)) {
          if (typeof target === 'string' && /^\d{4}-\d{2}$/.test(target)) set.add(target)
        }
      }
    }
    return [...set]
  }, [data, month, v2Lids])

  // Résolveur lid→log (pour tarifMenage = forfait ménage injecté à la projection).
  const getLogById = useCallback(
    (lid) => getAllLogsFlat(cfg).find((l) => l.id === lid) || null,
    [cfg]
  )

  // mk "YYYY-MM" → libellé "Mai 2026" (matche invoices.meta.mois pour le gel).
  const monthKeyToLabel = useCallback((mk) => {
    if (!mk) return ''
    const [yy, mm] = mk.split('-').map(Number)
    return `${MONTHS[mm - 1]} ${yy}`
  }, [])

  // `data` PROJETÉ : substitue UNIQUEMENT les reservations des lid 'v2' sur les
  // mois non gelés. COURT-CIRCUIT : v2Lids vide → dataProjete === data (MÊME
  // référence) → zéro régression mécanique (état prod actuel). Le skippedSink
  // est repeuplé à chaque recompute.
  const dataProjete = useMemo(() => {
    if (v2Lids.length === 0) return data
    const sink = {}
    // Lot 2 V2-delete-resa : collecteur des résas retirées par (lid, mk).
    const tombstoneSink = {}
    // Overrides ménage manuels (M3.5) — agrégés depuis data[mk][lid].menageOverrides.
    const menageOverrides = collectMenageOverrides(data, v2Lids)
    // Overrides MONTANT net hôte manuels (L1 V2-edit-montant) — depuis
    // data[mk][lid].montantOverrides. Appliqués APRÈS le ménage dans la projection.
    const montantOverrides = collectMontantOverrides(data, v2Lids)
    const projected = projectDataForFacturation(data, {
      v2Lids,
      resasByLogement,
      getLog: getLogById,
      monthKeys: projectionMonthKeys,
      invoicesRaw,
      monthKeyToLabel,
      menageOverrides,
      montantOverrides,
      skippedSink: sink,
      tombstoneSink,
    })
    skippedV2Ref.current = sink
    tombstonedV2Ref.current = tombstoneSink
    return projected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, v2Lids, resasByLogement, projectionMonthKeys, invoicesRaw, getLogById, monthKeyToLabel])

  // Wrapper qui referme sur cfg/dataProjete/month courants. La logique pure est
  // dans lib/calc.js (testable, réutilisable pour scénarios tarifaires futurs).
  // PR #169b : agrège résas direct internes (pricing_blocks) via mergeCalc.
  // WAGON M : lit `dataProjete` (= `data` tant qu'aucun logement n'est 'v2').
  // Commission = celle de la facture, arrondie ligne par ligne : calcPure
  // l'obtient de src/lib/commission-lignes.js (défaut du 2026-09-30).
  //
  // `c.total` reste commission + ménage (Annuel, Statistiques…) : ce
  // n'est PAS ce qu'on facture. Pour « À facturer », voir aFacturer ci-dessous.
  const calc = (lid, taux, mk = month) => {
    const base = calcPure(cfg, dataProjete, lid, taux, mk)
    const direct = calcDirectInternePure(blocksByMonth, lid, taux, mk)
    return mergeCalc(base, direct)
  }

  // « À FACTURER » = le « Total à régler » de la facture, au centime (décision
  // Dimitri 2026-10-01) : commission + ménage + prestations + remplacements
  // refacturés + ajustements. Source UNIQUE : totauxFacture, la fonction dont
  // le PDF imprime le total — mêmes données (dataProjete, incidents) que le
  // wrapper genFactureHTML plus bas. Avant : l'écran disait commission +
  // ménage (sept. 2026, l10 : 113,14 € à l'écran, 340,37 € facturés).
  // « Net propriétaire » = CA brut − le total EXACT de la facture, au centime
  // (netProprietaireFacture, src/lib/facture-html.js — revue #1559). La part
  // des résas directes FBZ, que calc() ajoute au CA mais que la facture ne
  // porte pas, garde son net. Mois affiché seulement. Un montant incalculable
  // reste NaN, que fmtE affiche comme tel (jamais 0).
  const aFacturer = (log) => {
    const t = totauxFacture({ log, cfg, data: dataProjete, incidents, month, y, m })
    const c = calc(log.id, log.taux)
    return { ...t, net: netProprietaireFacture(t, c) }
  }
  // Les lignes que la facture ajoute à commission + ménage, celles qui ne sont
  // pas à zéro — pour que l'écran s'additionne jusqu'à « À facturer ».
  const lignesEnPlusFacture = (t) => [
    ['Prestations', t.totalExtras],
    ['Remplacements', t.totalRemplacements],
    ['Ajustements', t.totalAjustements],
  ].filter(([, v]) => v !== 0)
  return {
    blocksByMonth, invalidatePricingBlocks, v2Lids, resasByLogement, skippedV2Ref, tombstonedV2Ref,
    getLogById, monthKeyToLabel, dataProjete, calc, aFacturer, lignesEnPlusFacture,
  }
}
